/**
 * @description Merchant Transactions page — Invoices / Estimates / Recurring tabs.
 * @dependencies InvoiceList, EstimateList, RecurringPlansList, react-router
 * @notes `?tab=invoices|estimates|recurring` selects the initial tab so Reporting
 * drill-down lands on Invoices with InvoiceList query params intact.
 */

import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { useUser } from '../contexts/AuthContext';
import { transactionsService, customersService } from '../services/api';
import { formatCurrency } from '../utils/invoiceHelpers';
import { getCustomerDisplayName, getCustomerSearchString } from '../utils/customerHelpers';
import StatusBadge from './shared/StatusBadge';
import PermissionGatedDropdown from './shared/PermissionGatedDropdown';
import { PERMISSIONS } from '../constants/permissionMappings';
import InvoiceList from './invoices/InvoiceList';
import EstimateList from './estimates/EstimateList';
import RecurringPlansList from './recurring/RecurringPlansList';

function Transactions() {
  const [transactions, setTransactions] = useState([]);
  const [customers, setCustomers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    date: '',
    status: 'All',
    search: ''
  });
  const { user } = useUser();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const tab = (searchParams.get('tab') || '').toLowerCase();
    if (tab === 'estimates') return 'Estimates';
    if (tab === 'recurring' || tab === 'recurring-plans') return 'Recurring Plans';
    return 'Invoices';
  });
  const [showRecurringCreateModal, setShowRecurringCreateModal] = useState(false);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef(null);
  const [retryKey, setRetryKey] = useState(0);

  // Retry function for error handling
  const retryFetch = () => {
    setRetryKey(prev => prev + 1);
  };

  // Fetch all data with proper cancellation
  useEffect(() => {
    if (!user?.id) return;
    
    const abortController = new AbortController();
    
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch customers and transactions in parallel
        const [customersData, transactionsData] = await Promise.all([
          customersService.getMerchantCustomers(),
          transactionsService.getMerchantTransactions()
        ]);
        
        // Only update state if request wasn't cancelled
        if (!abortController.signal.aborted) {
          // Create customer lookup map for efficient access
          const customerMap = {};
          if (customersData && Array.isArray(customersData)) {
            customersData.forEach(customer => {
              customerMap[customer.id] = customer;
            });
          }
          
          setCustomers(customerMap);
          setTransactions(transactionsData || []);
        }
      } catch (err) {
        // Only handle error if request wasn't cancelled
        if (!abortController.signal.aborted) {
          console.error('Error fetching data:', err);
          setError('Failed to load transactions. Please try again.');
          setTransactions([]);
          setCustomers({});
        }
      } finally {
        // Always clear loading state (prevents stuck spinner)
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };
    
    fetchData();
    
    // Cleanup function - cancels ongoing requests
    return () => {
      abortController.abort();
    };
  }, [user?.id, retryKey]);

  // Add click-outside handler for status dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setIsStatusDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clean industry-standard customer display
  const getCustomerName = (transaction) => {
    return getCustomerDisplayName(transaction, customers);
  };


  // Filter transactions
  const filteredTransactions = transactions.filter(tx => {
    const matchesDate = !filters.date || tx.date?.startsWith(filters.date);
    const matchesStatus = filters.status === 'All' || tx.status === filters.status;
    
    if (!filters.search) return matchesDate && matchesStatus;
    
    const searchLower = filters.search.toLowerCase();
    const matchesSearch = 
      tx.transactionReference?.toLowerCase().includes(searchLower) ||
      getCustomerSearchString(tx, customers).includes(searchLower);
    
    return matchesDate && matchesStatus && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
          <button 
            onClick={retryFetch} 
            className="ml-4 text-sm underline hover:no-underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[2000px] w-full mx-auto px-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Transactions</h1>
        <PermissionGatedDropdown
          items={[
            { label: 'Invoice', permission: PERMISSIONS.invoices.create, onClick: () => navigate('/merchant/invoices/new') },
            { label: 'Estimate', permission: PERMISSIONS.estimates.create, onClick: () => navigate('/merchant/estimates/new') },
            { label: 'Recurring Plan', permission: PERMISSIONS.recurring.create, onClick: () => { setActiveTab('Recurring Plans'); setShowRecurringCreateModal(true); } },
          ]}
          buttonClassName="bg-brand text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2 hover:bg-brand-hover transition-colors focus:outline-none"
        />
      </div>

      {/* Tabs */}
      <div className="border-b mb-6">
        <nav className="flex space-x-8">
          {['Estimates', 'Invoices', 'Recurring Plans', 'Transaction History'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab 
                  ? 'border-brand text-brand' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'Transaction History' && (
        <>
          {/* Filters */}
          <div className="bg-white p-4 rounded-lg shadow mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input
                type="date"
                value={filters.date}
                onChange={(e) => setFilters({...filters, date: e.target.value})}
                className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand"
                placeholder="Filter by date"
              />
              <div className="relative" ref={statusDropdownRef}>
                <button
                  onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
                  className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-lg font-semibold flex items-center justify-between hover:bg-gray-300 transition-colors"
                >
                  {filters.status}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isStatusDropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg py-1">
                    {['All', 'APPROVAL', 'DECLINE', 'refunded', 'partially_refunded', 'voided'].map(status => (
                      <button
                        key={status}
                        onClick={() => {
                          setFilters({...filters, status});
                          setIsStatusDropdownOpen(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        {(() => {
                          switch(status) {
                            case 'APPROVAL': return 'Approved';
                            case 'DECLINE': return 'Declined';
                            case 'refunded': return 'Refunded';
                            case 'partially_refunded': return 'Partial Refund';
                            case 'voided': return 'Voided';
                            default: return status;
                          }
                        })()}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <input
                type="text"
                placeholder="Search by reference or customer..."
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>
          </div>

          {/* Transactions Table */}
          {filteredTransactions.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <svg 
                className="mx-auto h-12 w-12 text-gray-400" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth="2" 
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" 
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No transactions found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {filters.date || filters.status !== 'All' || filters.search 
                  ? 'Try adjusting your filters' 
                  : 'Transactions will appear here once processed'}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Card Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Entry Mode</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Status</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredTransactions.map(tx => (
                      <tr key={tx.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {getCustomerName(tx)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {(() => {
                            const ref = tx.transactionReference || tx.id;
                            const isLinked = tx.allocatedAmount > 0 || tx.allocated_amount > 0;
                            if (ref.startsWith('EXT-')) {
                              return `External - ${ref}`;
                            }
                            return (
                              <span className="flex items-center gap-1">
                                {isLinked && (
                                  <svg className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                  </svg>
                                )}
                                {ref}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                          {tx.date ? format(new Date(tx.date), 'MMM d, yyyy') : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden lg:table-cell">
                        {tx.brand || tx.cardType || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={tx.transactionType === 'refund' ? 'text-orange-600' : 'text-gray-900'}>
                            {formatCurrency(tx.totalChargedAmount || tx.amount || 0)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                          <StatusBadge type="entry_mode" value={tx.entryMode} size="xs" />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap hidden lg:table-cell">
                          <StatusBadge 
                            type="transaction_status" 
                            value={{
                              status: tx.status,
                              paymentStatus: tx.transactionType === 'refund' ? 'paid' : undefined,
                              isVoided: tx.isVoided,
                              settlementStatus: tx.transactionType === 'refund' ? 'settled' : undefined
                            }}
                            size="xs"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <Link 
                          to={`/merchant/transactions/${tx.transactionReference || tx.id}`} 
                          className="text-brand hover:text-brand-hover"
                        >
                          View
                        </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'Estimates' && <EstimateList />}

      {activeTab === 'Invoices' && <InvoiceList />}

      {activeTab === 'Recurring Plans' && (
        <RecurringPlansList
          showCreateModal={showRecurringCreateModal}
          onCreateModalClose={() => setShowRecurringCreateModal(false)}
        />
      )}
    </div>
  );
}

export default Transactions;